// 📅 Φίλτρο περιόδου για τους καθαρισμούς (κοινό: CleaningDashboard + CleaningJobsAdmin).
// Εβδομάδα = Κυριακή→Σάββατο, ώστε το turnaround Σάββατο (cleaned_date = ημ. ναύλου) να είναι
// η ΤΕΛΕΥΤΑΙΑ μέρα της εβδομάδας. Έτσι η "τρέχουσα εβδομάδα" δείχνει το ΕΠΟΜΕΝΟ Σάββατο
// καθαρισμού και ΚΡΥΒΕΙ τα προηγούμενα (default = μόνο τρέχουσα, όχι παλιότερες).

export type CleaningPeriod =
  | { mode: 'week'; offset: number }               // offset 0 = τρέχουσα, -1 προηγ., +1 επόμ.
  | { mode: 'range'; from: string; to: string };   // ISO yyyy-mm-dd, inclusive (κενό = ανοιχτό όριο)

export const defaultPeriod: CleaningPeriod = { mode: 'week', offset: 0 };

// τοπική ημ/νία (χωρίς ώρα/ζώνη) → ISO yyyy-mm-dd
const toISO = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Αρχή εβδομάδας (Κυριακή 00:00) που περιέχει το ref, + offset εβδομάδες
const weekStartDate = (ref: Date, offset = 0): Date => {
  const d = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  d.setDate(d.getDate() - d.getDay() + offset * 7); // getDay(): Κυρ=0 … Σαβ=6
  return d;
};

// {from: Κυριακή, to: Σάββατο} ISO — inclusive
export const weekRange = (ref: Date, offset = 0): { from: string; to: string } => {
  const s = weekStartDate(ref, offset);
  const e = new Date(s); e.setDate(e.getDate() + 6);
  return { from: toISO(s), to: toISO(e) };
};

// Το εύρος ISO μιας period ("τώρα" = ref για week mode)
export const periodRange = (p: CleaningPeriod, now: Date): { from: string; to: string } =>
  p.mode === 'week' ? weekRange(now, p.offset) : { from: p.from || '', to: p.to || '' };

// Φιλτράρει jobs με cleaned_date εντός [from, to] inclusive.
// Job χωρίς cleaned_date → κρύβεται (δεν ξέρουμε σε ποια εβδομάδα ανήκει).
export const filterJobsByPeriod = (jobs: any[], p: CleaningPeriod, now: Date): any[] => {
  const { from, to } = periodRange(p, now);
  return jobs.filter(j => {
    const d = String(j.cleaned_date || '').slice(0, 10);
    if (!d) return false;
    return (!from || d >= from) && (!to || d <= to);
  });
};

// 🛡️ Είναι το cleaned_date στο ΜΕΛΛΟΝ (μετά από σήμερα); — guard για "μαρκάρισμα καθαρίστηκε"
export const isFutureISO = (iso: string, now: Date): boolean => {
  const d = String(iso || '').slice(0, 10);
  if (!d) return false;
  return d > toISO(now);
};

// Ετικέτα εβδομάδας dd/mm–dd/mm/yyyy (για UI + export header)
export const weekLabel = (ref: Date, offset = 0): string => {
  const { from, to } = weekRange(ref, offset);
  const dm = (iso: string) => { const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}` : iso; };
  return `${dm(from)}–${dm(to)}/${to.slice(0, 4)}`;
};

// Ετικέτα περιόδου (για export header)
export const periodLabel = (p: CleaningPeriod, now: Date): string => {
  if (p.mode === 'week') return p.offset === 0 ? `Τρέχουσα εβδομάδα (${weekLabel(now, 0)})` : weekLabel(now, p.offset);
  const eu = (iso: string) => { const m = (iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}/${m[2]}/${m[1]}` : ''; };
  const f = eu(p.from), t = eu(p.to);
  if (f && t) return `${f} – ${t}`;
  if (f) return `από ${f}`;
  if (t) return `έως ${t}`;
  return 'Όλες οι ημερομηνίες';
};
